<div class="service-billing-setup-title-container">
	<h3 class="header-text header-margin-bottom" i18n="@@billingContract-serviceBillingSetup">Service Billing Setup</h3>
	<h4 class="header-sub-text" i18n="@@billingContract-enterDetailsForNewBillingCustomer">
		Enter details for a new billing customer
	</h4>
</div>
<mat-divider></mat-divider>
<form [formGroup]="addContractFormGroup">
	<!-- Service billing setup section -->
	<div class="billing-setup-grid">
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column-span-two-cols"
		>
			<mat-label>{{ filterLabels.companyName }}</mat-label>
			<input
				matInput
				[matAutocomplete]="companyNameAutocomplete"
				formControlName="company"
				autocomplete="off"
				#companyNameTrigger="matAutocompleteTrigger"
				(input)="onCompanyNameTextChange($event, companyNameTrigger)"
				data-automation-id="company-name-input"
			/>
			<mat-autocomplete
				disableOptionCentering
				#companyNameAutocomplete="matAutocomplete"
				[displayWith]="displayCompanyName"
				data-automation-id="company-name-picker"
			>
				@for (option of filteredCompanyOptions | async; track option.id) {
					<mat-option
						[value]="option"
						(onSelectionChange)="onCompanySelected($event)"
						[attr.data-automation-id]="'company-name-' + option.name"
					>
						{{ option.name }}
					</mat-option>
				}
			</mat-autocomplete>
		</mat-form-field>
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.netsuiteCustomerInternalId }}</mat-label>
			<input
				matInput
				formControlName="netsuiteCustomerInternalId"
				autocomplete="off"
				data-automation-id="netsuite-customer-id-input"
			/>
		</mat-form-field>

		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column-span-two-cols"
		>
			<mat-label>{{ filterLabels.contractName }}</mat-label>
			<input matInput formControlName="contractName" autocomplete="off" data-automation-id="contract-name-input" />
		</mat-form-field>
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.contractId }}</mat-label>
			<input matInput formControlName="contractId" autocomplete="off" data-automation-id="contract-id-input" />
		</mat-form-field>

		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.unitOfMeasure }}</mat-label>
			<mat-select
				disableOptionCentering
				formControlName="unitOfMeasure"
				panelClass="thin-outline-select-panel"
				data-automation-id="unit-of-measure-picker"
			>
				@for (option of unitsOfMeasureOptions; track option) {
					<mat-option [value]="option.key" [attr.data-automation-id]="'unit-of-measure-' + option.value">
						{{ option.value }}
					</mat-option>
				}
			</mat-select>
		</mat-form-field>

		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.currencyType }}</mat-label>
			<mat-select
				disableOptionCentering
				formControlName="currencyType"
				panelClass="thin-outline-select-panel"
				data-automation-id="currency-type-picker"
			>
				@for (option of currencyTypeOptions; track option) {
					<mat-option [value]="option.key" [attr.data-automation-id]="'currency-type-' + option.value">
						{{ option.value }}
					</mat-option>
				}
			</mat-select>
		</mat-form-field>
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.unitPrice }}</mat-label>
			<input
				matInput
				formControlName="unitPrice"
				autocomplete="off"
				(keypress)="isNumberOrPeriodKey($event)"
				data-automation-id="unit-price-input"
			/>
		</mat-form-field>

		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.billingStartDate }}</mat-label>
			<input
				matInput
				formControlName="billingStartDate"
				[min]="minBillingStartDate"
				[matDatepicker]="billingStartDateDatepicker"
				autocomplete="off"
				data-automation-id="start-datepicker-input"
			/>
			<mat-datepicker-toggle matSuffix [for]="billingStartDateDatepicker" data-automation-id="start-datepicker-toggle">
				<mat-icon matDatepickerToggleIcon svgIcon="sl-nav-calendar"></mat-icon>
			</mat-datepicker-toggle>
			<mat-datepicker #billingStartDateDatepicker data-automation-id="start-datepicker"></mat-datepicker>
		</mat-form-field>
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.billingTrigger }}</mat-label>
			<mat-select
				disableOptionCentering
				formControlName="billingTrigger"
				panelClass="thin-outline-select-panel"
				data-automation-id="billing-trigger-picker"
			>
				@for (option of billingTriggerOptions; track option) {
					<mat-option [value]="option.key" [attr.data-automation-id]="'billing-trigger-' + option.value">
						{{ option.value }}
					</mat-option>
				}
			</mat-select>
		</mat-form-field>
		<mat-form-field
			subscriptSizing="dynamic"
			appearance="outline"
			class="thin-outline-input-field billing-setup-column"
		>
			<mat-label>{{ filterLabels.durationInMonths }}</mat-label>
			<input
				matInput
				formControlName="durationInMonths"
				autocomplete="off"
				(keypress)="isNumberKey($event)"
				data-automation-id="duration-input"
			/>
		</mat-form-field>
	</div>

	<!-- "Annual Increase" and "Device Leases" switch containers -->
	<div class="annual-inc-and-device-lease-container">
		<div class="gray-container" [class.activeContainer]="!addContractFormGroup.get('annualIncToggle')?.value">
			<div class="annual-inc-and-device-container">
				<div>
					<h3 class="header-text header-smaller-margin-bottom">
						{{ filterLabels.annualIncrease }}
					</h3>
					<h4 class="header-sub-text" i18n="@@billingContract-adjustBillingRates">
						Adjust billing rates for the upcoming year
					</h4>
				</div>
				<mat-slide-toggle
					formControlName="annualIncToggle"
					data-automation-id="annual-increase-toggle"
				></mat-slide-toggle>
			</div>
			<div>
				<mat-form-field
					subscriptSizing="dynamic"
					appearance="outline"
					class="thin-outline-input-field thin-outline-input-field-bkg-color-white width-one-hundred-percent annual-adj-rate-input-container"
				>
					<mat-label>{{ filterLabels.adjustmentRate }}</mat-label>
					<input
						matInput
						formControlName="annualIncAdjustmentRate"
						autocomplete="off"
						(keypress)="isNumberOrPeriodKey($event)"
						data-automation-id="annual-increase-input"
					/>
					<p matSuffix class="thin-outline-input-field-suffix-text">%</p>
				</mat-form-field>
			</div>
		</div>

		<div
			class="gray-container device-lease-container"
			[class.activeContainer]="!addContractFormGroup.get('deviceLeaseToggle')?.value"
		>
			<div class="annual-inc-and-device-container">
				<div>
					<h3 class="header-text header-smaller-margin-bottom">
						{{ filterLabels.deviceLease }}
					</h3>
					<h4 class="header-sub-text" i18n="@@billingContract-deviceLeaseDesc">
						A payment plan used to finance the purchase of devices.
					</h4>
				</div>
				<mat-slide-toggle
					formControlName="deviceLeaseToggle"
					data-automation-id="device-lease-toggle"
				></mat-slide-toggle>
			</div>
		</div>
	</div>

	<!-- "Billing End Date" radio button container -->
	<h3 class="header-text header-margin-bottom">
		{{ filterLabels.billingEndDate }}
	</h3>
	<mat-radio-group class="thin-outline-radio-button-group billing-end-date-container" formControlName="autoRenewToggle">
		@for (option of billingEndDateOptions; track option) {
			<mat-radio-button
				class="radio-button-container"
				[value]="option.key"
				[checked]="(addContractFormGroup.get('autoRenewToggle')?.value === true) === option.key"
				[attr.data-automation-id]="'end-date-' + option.key"
			>
				<div class="radio-button-header-container">
					<h3 class="radio-button-title">{{ option.valueTitle }}</h3>
					@if (option.valueTitleSmallerText) {
						<h3 class="radio-button-title radio-button-title-smaller-text">
							{{ option.valueTitleSmallerText }}
						</h3>
					}
				</div>
				<p class="radio-button-desc">{{ option.valueDesc }}</p>
			</mat-radio-button>
		}
	</mat-radio-group>
</form>

<!-- Add-On Billing container -->
<div class="add-on-billing-container" [formGroup]="addOnBillingFormGroup">
	<div class="add-on-billing-header-container">
		<h3 class="header-text header-margin-bottom" i18n="@@billingContract-addOnBillingTitle">Add-On Billing</h3>
		<h3
			class="header-text header-margin-bottom header-text-title-smaller-text"
			i18n="@@billingContract-addOnBillingTitleSmallerText"
		>
			(optional)
		</h3>
	</div>
	<mat-divider></mat-divider>
	<div class="add-on-billing-container-grid">
		<h4
			class="add-on-billing-grid-title add-on-billing-column-span-two-cols"
			i18n="@@billingContract-addOnBillingDescription"
		>
			Description
		</h4>
		<h4 class="add-on-billing-grid-title" i18n="@@billingContract-addOnBillingQuantity">Quantity</h4>
		<h4 class="add-on-billing-grid-title" i18n="@@billingContract-addOnBillingUnitPrice">Unit Price</h4>
		<h4 class="add-on-billing-grid-title" i18n="@@billingContract-addOnBillingMRCOrNRC">MRC / NRC</h4>
	</div>
	@for (item of getControls(); track item; let i = $index) {
		<div formArrayName="addOnBillingFormArray" [attr.data-automation-id]="'add-on-billing-' + i + '-container'">
			<div [formGroupName]="i" class="add-on-billing-container-grid">
				<mat-form-field
					subscriptSizing="dynamic"
					appearance="outline"
					class="thin-outline-input-field add-on-billing-column-span-two-cols"
					[ngClass]="{
						'thin-outline-input-field-force-invalid-outline': !isValidControlForOnBillingFormGroup(item, 'description'),
					}"
				>
					<input
						matInput
						formControlName="description"
						autocomplete="off"
						[attr.data-automation-id]="'add-on-billing-description-' + i + '-input'"
					/>
				</mat-form-field>
				<mat-form-field
					subscriptSizing="dynamic"
					appearance="outline"
					class="thin-outline-input-field"
					[ngClass]="{
						'thin-outline-input-field-force-invalid-outline': !isValidControlForOnBillingFormGroup(item, 'quantity'),
					}"
				>
					<input
						matInput
						formControlName="quantity"
						autocomplete="off"
						(keypress)="isNumberKey($event)"
						[attr.data-automation-id]="'add-on-billing-quantity-' + i + '-input'"
					/>
				</mat-form-field>
				<mat-form-field
					subscriptSizing="dynamic"
					appearance="outline"
					class="thin-outline-input-field"
					[ngClass]="{
						'thin-outline-input-field-force-invalid-outline': !isValidControlForOnBillingFormGroup(item, 'unitPrice'),
					}"
				>
					<input
						matInput
						formControlName="unitPrice"
						autocomplete="off"
						(keypress)="isNumberOrPeriodKey($event)"
						[attr.data-automation-id]="'add-on-billing-unit-price-' + i + '-input'"
					/>
				</mat-form-field>
				<mat-form-field
					subscriptSizing="dynamic"
					appearance="outline"
					class="thin-outline-input-field billing-setup-column"
					[ngClass]="{
						'thin-outline-input-field-force-invalid-outline': !isValidControlForOnBillingFormGroup(
							item,
							'recurringChargeTypeId'
						),
					}"
				>
					<mat-select
						disableOptionCentering
						formControlName="recurringChargeTypeId"
						panelClass="thin-outline-select-panel"
						[attr.data-automation-id]="'add-on-billing-mrcornrc-' + i + '-picker'"
					>
						<mat-label></mat-label>
						@for (option of mrcOrNrcOptions; track option) {
							<mat-option
								[value]="option.key"
								[attr.data-automation-id]="'add-on-billing-mrcornrc-' + i + '-' + option.value"
							>
								{{ option.value }}
							</mat-option>
						}
					</mat-select>
				</mat-form-field>
				<div class="trash-can-icon-container">
					<mat-icon
						svgIcon="sl-trash"
						class="trash-can-icon"
						(click)="removeAddOnBillingOption(i)"
						[attr.data-automation-id]="'remove-add-on-billing-' + i + '-button'"
					></mat-icon>
				</div>
			</div>
		</div>
	}
	<button
		mat-stroked-button
		(click)="addAddOnBillingOption($event)"
		class="incremental-button"
		data-automation-id="add-contract-increment-button"
	>
		+
	</button>
</div>
